import {
	FILE_UPLOAD_CONCURRENCY,
	processWithConcurrency,
	planChunkedObjectVideo,
	uploadFailureLogDetails,
	uploadServerRefusalLogDetails
} from "@uploads";
import {
	getFileUploadSessionById,
	IdbStallError,
	parseIdbStallMessage,
	updateFileUploadSession,
	type FileUploadSession
} from "@/db/fileUploadDb";
import { adminChunkedUploadDriver, inferFileCategory } from "@/services/chunkedUploadService";
import type { UploadTarget } from "@/types/uploadTarget";
import { createAdminPersistedTargetInitiator } from "./adminUploadTargetInitiator";
import { cancelAdminUploadSession } from "./adminUploadCancellation";
import { adminUploadTelemetry } from "./adminUploadTelemetry";

export interface DurableUploadBatchOptions {
	batchId: string;
	uploadScope?: { organizationId?: string; userId?: string };
	persistenceConcurrency?: number;
	captureEvidence?: (file: File) => Record<string, unknown> | undefined;
	isCancelled?: (index: number) => boolean;
	onSessionCreated?: (index: number, session: FileUploadSession) => void | Promise<void>;
	onPrepared?: (index: number, session: FileUploadSession) => void;
	/** Called when a prepared source is about to begin network target initiation. */
	onTargetStarting?: (index: number) => void;
	onTargetReady?: (index: number, target: UploadTarget) => void | Promise<void>;
	/**
	 * Resolves once file `index`'s transfer has settled (uploaded, deferred, failed or cancelled). Its
	 * upload turn — and so the right to mint the NEXT file's S3 upload — is held until then. Absent, a
	 * turn is released as soon as the file's target is ready.
	 */
	transferSettled?: (index: number) => Promise<void>;
	onPreparationProgress?: (index: number, persisted: number, total: number) => void;
	onFailed?: (index: number, error: unknown) => void;
	onDeferred?: (index: number, session: FileUploadSession, error: unknown) => void;
}

/**
 * LSP-23004: waits before retrying a source save that stalled in IndexedDB. The File is still in memory and a
 * stalled attempt abandons its own partial session, so a retry starts clean; only a stall is retried, every
 * other failure still fails the file at once. Two retries, then the file fails as before.
 */
export const SOURCE_PREPARATION_STALL_RETRY_DELAYS_MS: readonly number[] = [2_000, 5_000];

/**
 * What an IdbStallError says about the stall: the withIdbReopen phase ("operation", "operation retry", "reopen"), its
 * budget, and the payload when the stalled write carried one. Combined with how far the attempt got (session created,
 * chunks saved), that is what the logs can tell about WHAT stalled.
 */
function idbStallDetails(error: unknown) {
	const stall = error instanceof IdbStallError ? parseIdbStallMessage(error.message) : undefined;
	return {
		stallPhase: stall?.phase,
		stallTimeoutMs: stall?.timeoutMs,
		stallPayloadBytes: stall?.payloadBytes,
		stallPayloadIsUpperBound: stall ? stall.payloadIsUpperBound : undefined
	};
}

/**
 * A single durable session owns each file from source preparation through completion.
 *
 * Reserve at pick, mint lazily (docs/plans/2026-10-16-web-lazy-multipart-initiate.md): every source is
 * saved and — through the shared driver — reserved on the server as soon as it is picked, but a file's
 * S3 multipart upload is minted only when one of `FILE_UPLOAD_CONCURRENCY` upload turns is free, and the
 * turn is held until that file's transfer settles. Files the user never reaches keep a reserved server
 * row and no live multipart upload.
 */
export async function prepareDurableUploadTargets(
	files: File[],
	conversationId: string,
	projectChecklistItemId: string | undefined,
	options: DurableUploadBatchOptions
): Promise<Array<UploadTarget | undefined>> {
	const sessions: Array<FileUploadSession | undefined> = new Array(files.length);
	const targets: Array<UploadTarget | undefined> = new Array(files.length).fill(undefined);
	const sourceResolvers: Array<(source: { index: number; session: FileUploadSession | undefined }) => void> = [];
	const sourcePromises = files.map(() => new Promise<{ index: number; session: FileUploadSession | undefined }>((resolve) => sourceResolvers.push(resolve)));
	let readySourceCount = 0;
	const resolveSource = (index: number) => sourceResolvers[readySourceCount++]({ index, session: sessions[index] });
	// LSP-23004 per-batch breaker: once one file in this batch runs out of stall retries the store is treated as wedged,
	// and every later stall in this batch fails at once instead of waiting through its own retries. Scoped to this
	// call, so the next batch starts clean.
	let stallBreakerOpen = false;
	const tripStallBreaker = (index: number, attempts: number, error: IdbStallError) => {
		if (stallBreakerOpen) return;
		stallBreakerOpen = true;
		adminUploadTelemetry.logWarn("recording.fileUpload.sourcePreparationStallBreakerTripped", {
			conversationId,
			uploadBatchId: options.batchId,
			fileIndex: index,
			attempts,
			...idbStallDetails(error)
		});
	};
	const sourceWork = processWithConcurrency(files, options.persistenceConcurrency ?? FILE_UPLOAD_CONCURRENCY, async (file, index) => {
		if (options.isCancelled?.(index)) {
			resolveSource(index);
			return;
		}
		let sessionId: string | undefined;
		let attempt = 1;
		let failedByStallBreaker = false;
		// How far the current attempt got before it stalled: no session yet, or n of total chunks saved.
		let progress: { sessionCreated: boolean; chunksSaved: number; totalChunks?: number } = { sessionCreated: false, chunksSaved: 0 };
		try {
			const videoPlan = file.type.startsWith("video/")
				? await planChunkedObjectVideo(file, { telemetry: adminUploadTelemetry })
				: undefined;
			const persistSource = () =>
				adminChunkedUploadDriver.persistDeferredFileToIndexedDB({
					file,
					conversationId,
					projectChecklistItemId,
					batchId: options.batchId,
					uploadScope: options.uploadScope,
					fileCategory: inferFileCategory(file.type),
					captureEvidence: options.captureEvidence?.(file),
					onSessionCreated: async (session) => {
						// LSP-23004: an attempt that stalls never returns its id, so keep the last one created for the logs.
						sessionId = session.id;
						progress = { ...progress, sessionCreated: true, totalChunks: session.totalChunks };
						const offer = videoPlan && videoPlan.manifest.parts.length === session.totalChunks ? videoPlan : null;
						if (offer)
							await updateFileUploadSession(session.id, {
								transport: "chunked-object",
								chunkSizeBytes: offer.chunkSizeBytes,
								priorityChunkIndexesJson: JSON.stringify(offer.priorityChunkIndexes)
							});
						await options.onSessionCreated?.(index, session);
					},
					onPreparationProgress: (persisted, total) => {
						progress = { ...progress, chunksSaved: persisted, totalChunks: total };
						options.onPreparationProgress?.(index, persisted, total);
					}
				});
			for (;;) {
				try {
					// Each attempt reports only its own session: an earlier attempt's was abandoned (deleted) when it failed.
					progress = { sessionCreated: false, chunksSaved: 0 };
					sessionId = undefined;
					sessionId = await persistSource();
					break;
				} catch (error) {
					if (!(error instanceof IdbStallError) || options.isCancelled?.(index)) throw error;
					if (stallBreakerOpen) {
						failedByStallBreaker = true;
						throw error;
					}
					const delayMs = SOURCE_PREPARATION_STALL_RETRY_DELAYS_MS[attempt - 1];
					if (delayMs === undefined) {
						tripStallBreaker(index, attempt, error);
						throw error;
					}
					adminUploadTelemetry.logWarn("recording.fileUpload.sourcePreparationStallRetry", {
						conversationId,
						sessionId,
						uploadBatchId: options.batchId,
						fileIndex: index,
						attempt,
						delayMs,
						...progress,
						...idbStallDetails(error)
					});
					await new Promise((resolve) => setTimeout(resolve, delayMs));
					// A file cancelled during the wait ends like one cancelled before it started: no failure, but logged.
					if (options.isCancelled?.(index)) {
						adminUploadTelemetry.logInfo("recording.fileUpload.sourcePreparationCancelled", {
							conversationId,
							sessionId,
							uploadBatchId: options.batchId,
							fileIndex: index,
							attempts: attempt,
							during: "stall-retry-wait"
						});
						return;
					}
					// Another file tripped the breaker while this one waited: its stalled attempt stands as the last.
					if (stallBreakerOpen) {
						failedByStallBreaker = true;
						throw error;
					}
					attempt++;
				}
			}
			const session = await getFileUploadSessionById(sessionId);
			if (!session && !options.isCancelled?.(index)) throw new Error("The saved upload source is missing");
			if (!session || session.status === "cancelled") return;
			if (options.isCancelled?.(index)) {
				await cancelAdminUploadSession({ sessionId });
				return;
			}
			if (!session.sourcePrepared) throw new Error("The selected file was not fully saved");
			sessions[index] = session;
			options.onPrepared?.(index, session);
		} catch (error) {
			console.warn("[Upload] Source preparation failed before target reservation", {
				conversationId,
				sessionId,
				uploadBatchId: options.batchId,
				fileName: file.name,
				error
			});
			// LSP-23004: console.warn alone never reached a searchable Datadog field; the stall phase lives only in
			// the error message, which the browser log forwarder drops.
			const failure = {
				conversationId,
				sessionId,
				uploadBatchId: options.batchId,
				fileIndex: index,
				attempts: attempt,
				errorName: error instanceof Error ? error.name : typeof error,
				...progress,
				...idbStallDetails(error),
				// Why this stall was not retried: another file in the batch had already run out of retries.
				...(failedByStallBreaker ? { breakerOpen: true } : {})
			};
			// A row the user cancelled while its save was failing is cancelled, not "couldn't save": it is logged
			// apart from failures, and the dialog releases its target wait when the batch settles, exactly as for a
			// file cancelled before it started.
			if (options.isCancelled?.(index)) {
				adminUploadTelemetry.logInfo("recording.fileUpload.sourcePreparationCancelled", failure);
				return;
			}
			adminUploadTelemetry.logWarn("recording.fileUpload.sourcePreparationFailed", failure);
			options.onFailed?.(index, error);
		} finally {
			resolveSource(index);
		}
	});

	// Feed the upload turns in source-readiness order, so slow reads cannot occupy a turn.
	// Source preparation continues independently of the turns.
	const initiator = createAdminPersistedTargetInitiator();
	const deferTargetFailure = async (index: number, session: FileUploadSession, error: unknown) => {
		// LSP-23095: the raw error serialized as `{requestId}` alone, so a deferral never said why the target failed.
		console.warn("[Upload] Durable file target preparation deferred", {
			conversationId,
			sessionId: session.id,
			fileIndex: index,
			errorName: error instanceof Error ? error.name : typeof error,
			...uploadFailureLogDetails(error),
			...uploadServerRefusalLogDetails(error),
			error
		});
		let current: FileUploadSession | undefined;
		try {
			current = await getFileUploadSessionById(session.id);
		} catch (lookupError) {
			console.warn("[Upload] Could not inspect failed target preparation", {
				conversationId, sessionId: session.id, fileIndex: index, phase: "target-failure-recovery", error: lookupError
			});
			// LSP-23105: a stalled read says nothing about the row. Its source was saved (`sourcePrepared` was
			// checked before it reached target work), so it is deferred work the drain owns — not "couldn't
			// save, re-add it", which invites a duplicate of a file that later uploads on its own.
			if (options.isCancelled?.(index)) {
				console.info("[Upload] Target preparation ended after cancellation", {
					conversationId,
					sessionId: session.id,
					fileIndex: index,
					phase: "target-failure-recovery"
				});
				return;
			}
			options.onDeferred?.(index, session, error);
			return;
		}
		if (!current && !options.isCancelled?.(index)) options.onFailed?.(index, error);
		else if (current && current.status !== "cancelled") options.onDeferred?.(index, current, error);
		else console.info("[Upload] Target preparation ended after cancellation", { conversationId, sessionId: session.id, fileIndex: index });
	};
	const targetWork = processWithConcurrency(sourcePromises, FILE_UPLOAD_CONCURRENCY, async (sourcePromise) => {
		const { session, index } = await sourcePromise;
		if (!session) return;
		try {
			if (options.isCancelled?.(index)) {
				await cancelAdminUploadSession({ sessionId: session.id });
				return;
			}
			options.onTargetStarting?.(index);
			const target = await initiator.initiatePersisted({ session, stableId: session.id, totalChunks: session.totalChunks });
			const attachment = await adminChunkedUploadDriver.attachUploadTargetToDeferredSession({ sessionId: session.id, target });
			if (attachment === "attached") {
				const attached = await getFileUploadSessionById(session.id);
				if (attached) options.onPrepared?.(index, attached);
				targets[index] = target;
				let readinessFailed = false;
				await Promise.resolve()
					.then(() => options.onTargetReady?.(index, target))
					.catch(async (error) => {
						readinessFailed = true;
						await deferTargetFailure(index, session, error);
					});
				// Hold this upload turn until the transfer settles: the next file's upload is minted only then.
				if (!readinessFailed) await options.transferSettled?.(index);
				return;
			}
			// A cancelled/missing row cannot keep its just-minted target; a successor
			// already attached to that identity must keep it.
			const current = await getFileUploadSessionById(session.id);
			if (!current || current.status === "cancelled") {
				await initiator.abortPersisted?.(target);
				if (!current && !options.isCancelled?.(index)) throw new Error("The saved upload source disappeared during target creation");
			}
			else options.onDeferred?.(index, current, new Error("Another upload owner took over target attachment"));
		} catch (error) {
			await deferTargetFailure(index, session, error);
		}
	});
	await Promise.all([sourceWork, targetWork]);
	return targets;
}
